/**
 * TAL IN OCH TAL UT — omräkningen mellan det en människa skriver och det
 * kontraktet lagrar.
 *
 * ==========================================================================
 * ANVÄNDAREN SKRIVER ALDRIG EN KONTRAKTSENHET.
 *
 * shared/types.ts är absolut om enheterna — minuter, ören, meter och
 * tusendelar, allt som HELTAL i minsta enhet:
 *
 *     7,5 h      = 450 minuter
 *     1 295 kr   = 129500 ören
 *     4,5 mil    = 45000 meter
 *     2,5 m2     = 2500 tusendelar
 *
 * Montören skriver "7,5". Klienten räknar om. Skriv aldrig en egen parseFloat.
 * ==========================================================================
 *
 * VARFÖR EN EGEN FIL OCH INTE I Falt.tsx: den här modulen bär INGEN JSX och
 * INGEN React. Det gör den körbar rakt av under Node, och därmed PRÖVBAR —
 * test/klienten.ts räknar igenom varenda regel nedan. Pengaomräkningen är den
 * enda kod i klienten där ett fel blir kronor på någons faktura; den ska inte
 * ligga bakom en komponentfil som bara går att köra i en webbläsare.
 *
 * ui/index.ts speglar allt härifrån, så en skärm importerar som vanligt:
 *
 *     import { FAKTOR, tillHeltal } from '../ui';
 */

/**
 * Text -> heltal i minsta enhet. Användaren får skriva både komma och punkt;
 * blanksteg (och det tunna mellanslaget i "1 295") kastas.
 *
 * `faktor` är hur många av kontraktets enheter EN skriven enhet är:
 *   timmar -> minuter   60      "7,5"    -> 450
 *   kronor -> ören      100     "1295"   -> 129500
 *   mil    -> meter     10000   "4,5"    -> 45000
 *   m2     -> tusendel  1000    "2,5"    -> 2500
 *
 * AVRUNDNING SKER MED Math.round OCH INTE MED trunkering: "7,49" timmar blir
 * 449 minuter, inte 449,4. Ett trunkerat tal hade tappat en minut per rad och
 * en hel timme per månad ur någons fakturaunderlag.
 *
 * null = texten är inte ett tal (ännu). Tom sträng ger null, inte 0 — de är
 * inte samma sak: "inget ifyllt" får inte sparas som en nolltimme.
 */
export const tillHeltal = (text: string, faktor: number): number | null => {
  const rensad = text.replace(/[\s  ]/g, '').replace(',', '.');
  if (rensad === '') return null;
  if (!/^-?\d*\.?\d*$/.test(rensad) || rensad === '.' || rensad === '-') return null;
  const tal = Number(rensad);
  if (!Number.isFinite(tal)) return null;
  return Math.round(tal * faktor);
};

/** Heltal i minsta enhet -> text att visa. Motsatsen till tillHeltal. */
export const franHeltal = (heltal: number, faktor: number, decimaler = 1): string =>
  (heltal / faktor).toFixed(decimaler).replace('.', ',');

/** Vanliga faktorer. Namngivna så att ingen skriver 10000 på känsla. */
export const FAKTOR = {
  /** timmar -> Minutes */
  timmar_minuter: 60,
  /** kronor -> Ore */
  kronor_oren: 100,
  /** mil -> Meters */
  mil_meter: 10000,
  /** kilometer -> Meters */
  km_meter: 1000,
  /** en enhet -> Milli (materialantal i tusendelar) */
  enhet_tusendel: 1000,
  /** procent -> Hundredths (moms och ROT i hundradels procent) */
  procent_hundradel: 100,
} as const;
